import { type FormEvent, type ReactElement, useState } from 'react';
import { refreshSession, useAuthStore } from '@/entities/user';
import { BlockButton, TextField } from '@/shared/ui';
import { useIdentityVerification } from '../model/use-identity-verification';

/** Mock 본인인증 기본 입력 — 개발 모드에서 빠른 완주용. */
const MOCK_DEFAULT_BIRTH_DATE = '1990-01-01';
const MOCK_DEFAULT_PHONE = '01012345678';

interface IdentityStepProps {
  /** 인증 완료 시 계정 상태를 다시 불러오는 콜백. */
  readonly onDone: () => Promise<void>;
}

/** PASS 본인인증 화면 — mock 모드는 가짜 인증 폼으로 전체 흐름을 완주한다. */
export const IdentityStep = ({ onDone }: IdentityStepProps): ReactElement => {
  const user = useAuthStore((state) => state.user);
  const identity = useIdentityVerification();
  const [name, setName] = useState(user?.name ?? '');
  const [birthDate, setBirthDate] = useState(MOCK_DEFAULT_BIRTH_DATE);
  const [phone, setPhone] = useState(MOCK_DEFAULT_PHONE);
  const [isCompleting, setIsCompleting] = useState(false);

  const handleMockComplete = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setIsCompleting(true);

    try {
      const succeeded = await identity.completeMock({
        // 같은 CI 는 1계정 원칙이라 사용자별로 유일한 값을 쓴다. (개발 전용)
        ci: `mock-ci-${user?.id ?? 'anonymous'}`,
        name,
        birthDate,
        phone,
      });

      if (succeeded) {
        await refreshSession();
        await onDone();
      }
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <p className="border-l-2 border-brand-lime bg-muted/70 px-4 py-3 text-[15px] leading-relaxed">
        서비스 이용을 위해 PASS 본인인증이 필요합니다. (만 19세 이상)
      </p>

      {identity.error ? (
        <p
          role="alert"
          className="border-l-2 border-destructive bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          {identity.error}
        </p>
      ) : null}

      {!identity.prepared ? (
        <BlockButton
          type="button"
          className="mt-3"
          isLoading={identity.isPending}
          onClick={() => void identity.prepare()}
        >
          본인인증 시작
        </BlockButton>
      ) : identity.isMock ? (
        <form
          onSubmit={handleMockComplete}
          className="mt-3 flex flex-col gap-7 border border-dashed border-foreground/20 p-5"
          noValidate
        >
          <p className="text-xs text-muted-foreground">
            개발 모드(mock) — 실제 대행사 없이 인증 결과를 시뮬레이션합니다.
          </p>
          <TextField label="이름" value={name} onChange={(e) => setName(e.target.value)} />
          <TextField
            label="생년월일"
            type="date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
          />
          <TextField label="휴대폰 번호" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <BlockButton type="submit" isLoading={isCompleting}>
            Mock 인증 완료
          </BlockButton>
        </form>
      ) : (
        <div className="mt-3 flex flex-col gap-2.5">
          <BlockButton type="button" onClick={() => identity.openAuthWindow()}>
            인증창 열기
          </BlockButton>
          <BlockButton type="button" variant="outline" onClick={() => void onDone()}>
            인증을 마쳤어요
          </BlockButton>
        </div>
      )}
    </div>
  );
};
